// Tree presentation for the existing native-select-backed multi-select. Only
// native options may be submitted; structural ancestors are display-only codes.
export default class MultiSelectTree {
  constructor(component) {
    this.component = component
    this.nodes = new Map()
    this.query = ""
  }

  render(parent) {
    const { component } = this
    this.nodes.clear()
    component.wrapper.classList.add("form-multi-select-tree")
    component.toggler.setAttribute("aria-haspopup", "tree")
    parent.setAttribute("role", "tree")
    const append = (items, ancestor = null, depth = 0) => items.forEach((item, index) => {
      const row = document.createElement("div")
      row.className = "form-multi-select-option form-multi-select-option-with-checkbox"
      row.dataset.value = item.value
      row.style.setProperty("--tree-depth", depth)
      row.tabIndex = 0
      row.setAttribute("role", "treeitem")
      row.setAttribute("aria-level", depth + 1)
      row.setAttribute("aria-posinset", index + 1)
      row.setAttribute("aria-setsize", items.length)
      row.setAttribute("aria-label", item.text)
      const label = document.createElement("span")
      label.textContent = item.text
      row.append(label)
      const node = { ...item, row, parent: ancestor, expanded: true, children: item.children || [] }
      this.nodes.set(String(item.value), node)
      if (node.children.length) {
        row.classList.add("form-multi-select-tree-branch")
        const toggle = document.createElement("button")
        toggle.type = "button"
        toggle.className = "form-multi-select-tree-toggle"
        toggle.tabIndex = -1
        toggle.setAttribute("aria-label", item.text)
        toggle.textContent = "▸"
        node.toggle = toggle
        component.listen(toggle, "click", (event) => {
          event.stopPropagation()
          this.toggle(node)
          row.focus()
        })
        row.prepend(toggle)
      }
      parent.append(row)
      append(node.children, node, depth + 1)
    })
    append(component.config.tree)
    this.filter("")
  }

  descendants(node) {
    return [node, ...node.children.flatMap((child) => this.descendants(this.nodes.get(String(child.value))))]
  }

  optionsFor(node, filtered = false) {
    const codes = new Set(this.descendants(node).filter((child) => !filtered || child.matches).map((child) => String(child.value)))
    return [...this.component.select.options].filter((option) => codes.has(option.value) && !this.component.isDisabled(option))
  }

  nativeOptions(rows) {
    return [...new Set(rows.flatMap((row) => this.optionsFor(this.nodes.get(row.dataset.value), Boolean(this.query))))]
  }

  selectValue(value, force) {
    const node = this.nodes.get(value)
    if (!node) return
    // Backspace removes just the last selected department. Clicking a branch
    // operates on its full authorized subtree, even while searching.
    const options = force === false
      ? [...this.component.select.options].filter((option) => option.value === value)
      : this.optionsFor(node)
    this.component.toggleOptions(options, force ?? !options.every((option) => option.selected))
  }

  updateSelection() {
    for (const node of this.nodes.values()) {
      const options = this.optionsFor(node)
      const count = options.filter((option) => option.selected).length
      const checked = count > 0 && count === options.length
      const mixed = count > 0 && !checked
      node.row.classList.toggle("form-multi-selected", checked)
      node.row.classList.toggle("form-multi-select-indeterminate", mixed)
      node.row.classList.toggle("disabled", options.length === 0)
      node.row.tabIndex = options.length ? 0 : -1
      node.row.setAttribute("aria-disabled", String(options.length === 0))
      node.row.setAttribute("aria-checked", mixed ? "mixed" : String(checked))
      node.row.removeAttribute("aria-selected")
    }
  }

  filter(query) {
    this.query = query.trim().toLowerCase()
    for (const node of this.nodes.values()) {
      node.matches = !this.query || `${node.text} ${node.value}`.toLowerCase().includes(this.query) || Boolean(node.parent?.matches)
      node.visible = node.matches
    }
    // Search results retain their complete ancestor path without making those
    // context-only ancestors part of "select search results".
    for (const node of [...this.nodes.values()].reverse()) {
      if (node.visible && node.parent) node.parent.visible = true
    }
    for (const node of this.nodes.values()) {
      node.shown = node.visible && (!node.parent || (node.parent.shown && (this.query || node.parent.expanded)))
      node.row.style.display = node.shown ? "" : "none"
      if (node.toggle) {
        const expanded = Boolean(this.query) || node.expanded
        node.row.setAttribute("aria-expanded", expanded)
        node.toggle.setAttribute("aria-expanded", expanded)
      }
    }
  }

  toggle(node, expanded = !node.expanded) {
    node.expanded = expanded
    this.component.filter(this.query, false)
    this.component.popper?.update()
  }

  handleKeydown(event) {
    if (event.target.closest(".form-multi-select-tree-toggle") && ["Enter", " "].includes(event.key)) {
      event.preventDefault()
      event.target.click()
      return true
    }
    const row = event.target.closest('[role="treeitem"]')
    const node = row && this.nodes.get(row.dataset.value)
    if (!node || !["ArrowLeft", "ArrowRight"].includes(event.key)) return false
    event.preventDefault()
    if (event.key === "ArrowRight") {
      if (node.children.length && !node.expanded && !this.query) this.toggle(node, true)
      else if (node.children.length) this.nodes.get(String(node.children[0].value)).row.focus()
    } else if (node.children.length && node.expanded && !this.query) this.toggle(node, false)
    else node.parent?.row.focus()
    return true
  }

  snapshotState() {
    return [...this.nodes.values()].filter((node) => !node.expanded).map((node) => String(node.value))
  }

  restoreState(collapsed = []) {
    for (const value of collapsed) {
      const node = this.nodes.get(value)
      if (node) node.expanded = false
    }
    this.component.filter("", false)
  }
}
